import * as React from "react";
import { observer } from "mobx-react";
import { UitkCardAloha, UitkCardMedia, UitkCardAlohaLink, UitkCardAlohaContentSection } from "uitk-react-cards";
import { CampaignRecommendationsWithOffersQuery } from "../../../../../../__generated__/api";
import Offers = CampaignRecommendationsWithOffersQuery.Offers;
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { locKeys } from "components/shared/Merchandising/Inventory/l10n";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkPriceLockup } from "uitk-react-price-lockup";
import { withStores } from "stores";
import { RouteStore, QueryRoute } from "bernie-core";
import { Link } from "react-router-dom";
import { Layer } from "bernie-view";
import { InfoDialog } from "../../Common/InfoDialog/InfoDialog";
import { MabTrackEvent, withMabTracker } from "src/components/utility/analytics/MabTracker";
import { UitkLayoutPosition } from "uitk-react-layout-position";
import { UitkSpacing } from "uitk-react-spacing";
import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { Action } from "components/utility/analytics/FlexAnalyticsUtils";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkBadge, UitkDealMemberBadge, UitkVipBadge } from "uitk-react-badge";
import { UitkIcon, UitkIconSize } from "uitk-react-icons";
import { UitkHeading } from "uitk-react-text";
export interface HotelInventoryCardProps {
  offer: Offers;
  cardIndex: number;
  campaignId: string;
  openDialog: (dialogPath: string) => void;
  closeDialog: () => void;
  moduleName: string;
  border?: boolean;
  routeStore: RouteStore;
  mabTrack?: (x: MabTrackEvent) => null;
}
export const HotelInventoryCard = withMabTracker(
  withStores("routeStore")(
    observer(
      ({
        offer,
        cardIndex,
        campaignId,
        closeDialog,
        moduleName,
        border,
        routeStore,
        mabTrack,
      }: HotelInventoryCardProps) => {
        if (offer.__typename !== "HotelOfferItem") {
          return null;
        }
        const discountBadge = offer.discountBadge?.text;
        const isFreeCancellation = offer.freeCancellationMessage;
        const { formatText } = useLocalization();
        const strikeThroughPrice = offer?.lodgingOffer?.strikeThroughPrice?.value;
        const currentPrice = offer.lodgingOffer?.price?.value;
        const a11yPrice = offer.lodgingOffer?.price?.allyText || "";
        const priceDisclaimerLabel = formatText(locKeys.priceDisclaimerLabel);
        const disclaimerInfoDialog = () => (
          <QueryRoute query={{ pwaDialog: `disclaimer-info-${campaignId}-${cardIndex}` }}>
            <Layer id={`disclaimer-info-dialog-${campaignId}-${cardIndex}`} onDismiss={closeDialog}>
              <InfoDialog
                infoText={offer?.lodgingOffer?.price.disclaimerText}
                closeDialog={closeDialog}
                buttonText={formatText("DestinationsByAffinity.priceDialogCloseButton")}
              />
            </Layer>
          </QueryRoute>
        );
        const cardClicked = mabTrack
          ? () => {
              const mabTrackData: MabTrackEvent = {
                moduleName: "geo_hotels_1",
                eventTarget: "geo_hotels_1",
                eventType: "homepageClick.nonPath",
                rfrr: `MerchCampaign.card-${campaignId}-${cardIndex}`,
              };
              mabTrack(mabTrackData);
            }
          : undefined;

        return (
          <UitkCardAloha className="HotelInventoryCard" border={border}>
            <UitkCardMedia>
              <UitkFigure ratio={UitkFigureAspectRatioType.R21_9}>
                {offer?.mod?.modTagText ? (
                  <UitkLayoutPosition
                    type="absolute"
                    position={{ top: "three", left: "three", zindex: "layer3" }}
                    cloneElement={true}
                  >
                    <UitkDealMemberBadge text={offer?.mod?.modTagText}>
                      <UitkIcon name="mod" size={UitkIconSize.SMALL} />
                    </UitkDealMemberBadge>
                  </UitkLayoutPosition>
                ) : (
                  <>
                    {offer.vipMessage && !offer.mod && (
                      <UitkSpacing margin={{ blockstart: "three", inlinestart: "three" }}>
                        <UitkVipBadge className="hotelBadge" text={offer.vipMessage} />
                      </UitkSpacing>
                    )}
                  </>
                )}
                <LazyLoadedImage src={offer.image.url} alt={offer.image.description} />
              </UitkFigure>
            </UitkCardMedia>
            <div>
              <UitkCardAlohaContentSection>
                <div>
                  <UitkSpacing padding={{ blockend: "half" }}>
                    <UitkHeading level={3} size={500} className="truncate">
                      {offer.name}
                    </UitkHeading>
                  </UitkSpacing>
                  <span className="uitk-type-300">{offer.location}</span>
                  {offer.guestReviewRating && (
                    <UitkSpacing padding={{ inlineend: "one" }}>
                      <div className="reviewRating">
                        <UitkSpacing padding={{ inlineend: "one" }}>
                          <span className="uitk-type-heading-300">{offer.guestReviewRating}</span>
                        </UitkSpacing>
                        <span className="uitk-type-300 pwa-theme--grey-700">{offer.guestReviewCount}</span>
                      </div>
                    </UitkSpacing>
                  )}
                </div>
              </UitkCardAlohaContentSection>
              <UitkCardAlohaContentSection>
                {offer?.mod?.modAvailabilityText ? (
                  <div>
                    <hr className={discountBadge ? "separatorLine separatorLineWidth" : "separatorLine"} />
                    <div className="uitk-type-right uitk-type-200 badgeStyle">
                      <UitkDealMemberBadge text={offer?.mod?.modAvailabilityText} className="bold" />
                    </div>
                  </div>
                ) : (
                  <div>
                    {!offer.discountBadge?.text ? (
                      <UitkSpacing padding={{ blockend: "two" }}>
                        <hr className="separatorLine" />
                      </UitkSpacing>
                    ) : (
                      <>
                        <hr className="separatorLine separatorLineWidth" />
                        <div className="uitk-type-right badgeStyle">
                          {offer?.mod?.modTagText ? (
                            <UitkDealMemberBadge text={offer.discountBadge?.text} />
                          ) : (
                            <UitkBadge text={offer.discountBadge?.text} />
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
                <UitkLayoutGrid columns={2} space="two">
                  <>
                    <div>
                      {offer.freeCancellationMessage && (
                        <UitkSpacing padding={{ blockstart: "one", blockend: "two" }}>
                          <div className="uitk-type-300 uitk-type-bold freeCancellation">
                            {offer?.freeCancellationMessage}
                          </div>
                        </UitkSpacing>
                      )}
                      <div className={isFreeCancellation ? "uitk-type-200" : "uitk-type-200 dateStyle"}>
                        {offer?.dates}
                      </div>
                    </div>
                    <div className="uitk-type-right price">
                      {offer?.mod?.modSignInText ? (
                        <UitkLayoutGridItem className="priceLink" colStart={2} colEnd="end">
                          <TrackedLink
                            className="uitk-type-300 uitk-type-right bold"
                            moduleName={moduleName}
                            action={Action.CLICK}
                            rfrr={`card-${campaignId}-${cardIndex}-MOD-SignUp`}
                            onClick={cardClicked}
                            href={offer?.mod?.signInUrl?.relativePath}
                          >
                            {offer?.mod?.modSignInText}
                          </TrackedLink>
                        </UitkLayoutGridItem>
                      ) : (
                        <>
                          <UitkPriceLockup
                            lockupPrice={currentPrice}
                            priceSubtextFirst={offer.lodgingOffer?.price?.qualifierText}
                            a11yPrice={a11yPrice}
                            a11yStrikeThroughDialogTrigger={priceDisclaimerLabel}
                            strikeThroughPrice={strikeThroughPrice}
                            strikeThroughTrigger={
                              <Link
                                to={{
                                  pathname: routeStore.pathname,
                                  search: routeStore.mergeQueryString(routeStore.search, {
                                    pwaDialog: `disclaimer-info-${campaignId}-${cardIndex}`,
                                  }),
                                }}
                                className="disclaimerInfoIcon"
                              />
                            }
                          />
                          {disclaimerInfoDialog()}
                        </>
                      )}
                    </div>
                  </>
                </UitkLayoutGrid>
              </UitkCardAlohaContentSection>
              <UitkCardAlohaLink>
                {offer.lodgingOffer && (
                  <TrackedLink
                    moduleName={moduleName}
                    action={Action.CLICK}
                    href={offer.lodgingOffer?.actionLink.href}
                    rfrr={`card-${campaignId}-${cardIndex}`}
                    onClick={cardClicked}
                  >
                    {offer.lodgingOffer?.actionLink.text}
                  </TrackedLink>
                )}
              </UitkCardAlohaLink>
            </div>
          </UitkCardAloha>
        );
      }
    )
  )
);
